/**
 * The managed toolchain both sandbox templates bake, and the one way a run
 * turns a repository's chosen runtimes into a `PATH` and an environment.
 *
 * Everything under `root` is root-owned and read-only at run time: setup runs
 * unprivileged, so nothing is ever installed there during a run. Every path a
 * package manager writes to (virtualenvs, gem homes, Go caches, the uv cache)
 * lives in the checkout or in the run user's home.
 *
 * `QA_TOOLCHAIN` is the only list of baked versions. The template bakes exactly
 * these and preflight compares a repository's declared versions against
 * exactly these. A declared version the template does not bake is installed by
 * the run itself, into the run user's home, before its setup commands: the
 * baked versions are the instant path, never the only one.
 */
export const QA_TOOLCHAIN = {
    root: "/opt/toolchain",
    /** "24" is the sandbox image's own Node; "22" is baked beside it. */
    node: { "24": "24.21.0", "22": "22.23.3" },
    python: { "3.12": "3.12.14", "3.13": "3.13.15" },
    ruby: { "3.3": "3.3.12" },
    go: { "1.23": "1.23.12" },
    /** The .NET SDKs: the key is the line, the value the exact SDK release. */
    dotnet: { "8": "8.0.425", "10": "10.0.401" },
    /**
     * The PHP CLIs, by line. Unlike every other runtime the value is the line
     * itself, not a patch: they come from Ondřej Surý's Debian archive (the
     * Debian PHP maintainer's own builds, the ones setup-php uses), which serves
     * only the newest build of each line, so a rebuilt template takes that
     * line's newest patch and the version check asserts the line.
     */
    php: { "7.4": "7.4", "8.1": "8.1", "8.2": "8.2", "8.3": "8.3", "8.4": "8.4" },
    composer: "2.10.3",
    opentofu: "1.13.1",
    tflint: "0.64.0",
    pnpm: "10.34.5",
    yarnBerry: "4.9.2",
    yarn1: "1.22.22",
    poetry: "1.8.5",
    mise: "2026.9.12",
    uv: "0.12.18",
    docker: "28.3.0",
    compose: "2.38.1",
};
/**
 * The SHA-512 Microsoft's release metadata records for each baked SDK's
 * linux-x64 tarball (`dotnet/release-metadata/<channel>/releases.json`). The
 * image build checks each download against it before extracting anything.
 */
export const QA_DOTNET_SDK_SHA512 = {
    "8": "934b8060a7190e5909ad1fd0785db542f487b3bbf6cdd14826b02095fdd0d0394298b1634085eff302928fccc33f7c1a7253e9b87df555fc36fce819bcd2e798",
    "10": "51c8b999af9e8dd9998c9edc5944e19a90788862068acd38694e098889054ce8c23d4f0c5cccfa16bf187d044562359e5ee69a9f8ad0bbe913ba90311fbce25b",
};
/**
 * The PHP extensions every baked PHP line carries, as Surý's package suffixes:
 * what Composer's platform check and a typical CRM, framework or PHPUnit suite
 * load (`common` brings ctype, fileinfo, iconv, phar, tokenizer and the like;
 * `xml` brings dom, simplexml, xmlreader and xmlwriter; `mysql` brings mysqli,
 * mysqlnd and pdo_mysql). PHP 7.4 also needs `json`, built in from 8.0.
 */
export const QA_PHP_EXTENSIONS = [
    "common",
    "bcmath",
    "curl",
    "gd",
    "imap",
    "intl",
    "mbstring",
    "mysql",
    "opcache",
    "pgsql",
    "readline",
    "soap",
    "sqlite3",
    "xml",
    "zip",
];
/** The module names `php -m` lists for those extensions, checked in every baked line at build time. */
export const QA_PHP_MODULES = [
    "bcmath",
    "ctype",
    "curl",
    "dom",
    "gd",
    "iconv",
    "imap",
    "intl",
    "json",
    "mbstring",
    "mysqli",
    "pdo_mysql",
    "pdo_pgsql",
    "pdo_sqlite",
    "pgsql",
    "simplexml",
    "soap",
    "tokenizer",
    "xml",
    "xmlwriter",
    "zip",
];
/**
 * Surý's archive (packages.sury.org) and its keyring package, pinned by
 * SHA-256 so the image build trusts exactly the signing key it was reviewed
 * with: a rotated key fails the build instead of being taken on faith.
 */
export const QA_PHP_ARCHIVE = {
    keyringUrl: "https://packages.sury.org/debsuryorg-archive-keyring.deb",
    keyringSha256: "7511384559c9ddf1d5ce5f60be429ae9d4e7d01d9480d6f1b7a30c0810cf8b60",
    keyring: "/usr/share/keyrings/debsuryorg-archive-keyring.gpg",
    repository: "https://packages.sury.org/php/",
};
/**
 * The single-file downloads the template bakes beside the runtimes, each with
 * the SHA-256 its publisher records (getcomposer.org's `.sha256sum`, OpenTofu's
 * and TFLint's release checksums). OpenTofu is the open-source Terraform fork;
 * HashiCorp's own `terraform` (BSL-licensed) is deliberately not baked.
 */
export const QA_CLI_DOWNLOADS = {
    composer: {
        url: `https://getcomposer.org/download/${QA_TOOLCHAIN.composer}/composer.phar`,
        sha256: "7a2d379d5b8ffdaa028580ef26494c36d2feef4b178d3dd1473a4dbc5e17c8d6",
    },
    opentofu: {
        url: `https://github.com/opentofu/opentofu/releases/download/v${QA_TOOLCHAIN.opentofu}/tofu_${QA_TOOLCHAIN.opentofu}_linux_amd64.tar.gz`,
        sha256: "378ada19d4bc70c43732004e8159be771b23b9a5afdf059e5f8a2b3fa2c70a69",
    },
    tflint: {
        url: `https://github.com/terraform-linters/tflint/releases/download/v${QA_TOOLCHAIN.tflint}/tflint_linux_amd64.zip`,
        sha256: "cca9d13e2e1d7a2c627af60ff899a3c9b74212899416aeb96ec764d2ef954537",
    },
};
/** Where Microsoft publishes .NET SDKs and the release metadata that names their digests. */
export const QA_DOTNET_DOWNLOAD_HOST = "builds.dotnet.microsoft.com";
/** The linux-x64 tarball of an exact .NET SDK release. */
export function dotnetSdkUrl(version) {
    return `https://${QA_DOTNET_DOWNLOAD_HOST}/dotnet/Sdk/${version}/dotnet-sdk-${version}-linux-x64.tar.gz`;
}
/**
 * The workspace variable that carries a GitHub token with `read:packages` for
 * NuGet, npm and RubyGems sources on GitHub Packages
 * (`nuget.pkg.github.com/<owner>`, `npm.pkg.github.com` scopes,
 * `rubygems.pkg.github.com/<owner>`) and the ghcr.io images a repository's
 * compose files run. A GitHub App installation token cannot read
 * GitHub Packages, so a person sets this once for the workspace; builds and
 * setup runs then sign in to those sources with it, and it is never written
 * into a repository or onto disk.
 */
export const GITHUB_PACKAGES_TOKEN_VARIABLE = "GITHUB_PACKAGES_TOKEN";
/** An older name for the same token, read when the generic name is not set. */
export const NUGET_GITHUB_TOKEN_VARIABLE = "NUGET_GITHUB_TOKEN";
/** What the token is for and what it must be, in one sentence wherever it is asked for. */
export const GITHUB_PACKAGES_TOKEN_HELP = "Reads your organization's GitHub Packages (NuGet, npm, RubyGems and ghcr.io images) during setup and builds. " +
    "Use a classic personal access token with the read:packages scope, from an account that can read every organization " +
    "whose packages these repositories use. One token covers all repositories.";
/** GitHub's page for creating that token, with the scope and a description filled in. */
export const GITHUB_PACKAGES_TOKEN_URL = "https://github.com/settings/tokens/new?scopes=read:packages&description=YAGNI%20GitHub%20Packages";
/**
 * The value and name of the GitHub Packages token among a run's variables:
 * `GITHUB_PACKAGES_TOKEN`, else the older `NUGET_GITHUB_TOKEN`. Null when
 * neither is set.
 */
export function githubPackagesToken(variables) {
    for (const name of [GITHUB_PACKAGES_TOKEN_VARIABLE, NUGET_GITHUB_TOKEN_VARIABLE]) {
        const value = variables[name];
        if (value)
            return { name, value };
    }
    return null;
}
/**
 * The hosts a GitHub Packages registry serves from, per ecosystem: the
 * registry, and the storage GitHub redirects package downloads to (GitHub's
 * meta API lists both under `packages`). Opened whenever a repository's config
 * names a source there, whatever its owner: egress is per host, shared by every
 * owner, and without the token GitHub refuses every request.
 */
export const QA_GITHUB_PACKAGES_ORIGINS = {
    nuget: ["https://nuget.pkg.github.com", "https://nugetregistryv2prod.blob.core.windows.net"],
    // An npm tarball GET answers 302 to pkg-npm.githubusercontent.com (probed
    // 2026-10-11 from a workflow in YAGNI-App/bench-packages); the blob host is
    // the older storage, kept while GitHub may still answer with it.
    npm: ["https://npm.pkg.github.com", "https://pkg-npm.githubusercontent.com", "https://npmregistryv2prod.blob.core.windows.net"],
    rubygems: ["https://rubygems.pkg.github.com", "https://rubygemsregistryv2prod.blob.core.windows.net"],
    // A ghcr.io blob GET answers 307 to pkg-containers.githubusercontent.com.
    ghcr: ["https://ghcr.io", "https://pkg-containers.githubusercontent.com"],
};
const RUNTIMES = ["node", "python", "ruby", "go", "dotnet", "php"];
const RUNTIME_NAMES = {
    node: "Node",
    python: "Python",
    ruby: "Ruby",
    go: "Go",
    dotnet: ".NET SDK",
    php: "PHP",
};
/** The standard system directories, after everything the toolchain adds. */
const SYSTEM_PATH = ["/usr/local/sbin", "/usr/local/bin", "/usr/sbin", "/usr/bin", "/sbin", "/bin"];
const MISE_BAKED_DIR = `${QA_TOOLCHAIN.root}/mise`;
const UV_PYTHON_BAKED_DIR = `${QA_TOOLCHAIN.root}/python`;
/**
 * The mise settings file the image bakes (`miseSettingsToml` in
 * `toolchainPolicy.ts`). The pinned mise reads settings only from a config
 * file's `[settings]` table, so every run points `MISE_SYSTEM_CONFIG_FILE`
 * here; there is no `MISE_SETTINGS_FILE` in that release.
 */
export const QA_MISE_SETTINGS_PATH = `${MISE_BAKED_DIR}/settings.toml`;
/**
 * The mise backends refused by name: plugin runners (asdf, vfox), package
 * managers that run install scripts (cargo, npm, pipx/pypi, gem, go, spm,
 * dotnet's tool backend, conda), and downloaders with no checksum of their own
 * (ubi, which upstream deprecated, http, s3 and the forge release backends).
 * `dotnet` here is the `dotnet:<package>` backend for .NET global tools, not
 * the core `dotnet` SDK.
 */
export const REFUSED_BACKENDS = [
    "asdf",
    "vfox",
    "ubi",
    "cargo",
    "npm",
    "pipx",
    "pypi",
    "gem",
    "go",
    "spm",
    "dotnet",
    "conda",
    "http",
    "s3",
    "github",
    "gitlab",
    "forgejo",
];
/**
 * The mise settings the image bakes at `QA_MISE_SETTINGS_PATH` (spec §2), as
 * the `[settings]` table of a system config file, the only settings file the
 * pinned mise reads: the baked aqua registry, never floating; no cosign, SLSA
 * or GitHub attestation checks (each would add sigstore egress and a verifier
 * download; checksums stay on); no compiling; no auto-install of a missing
 * tool; no idiomatic version files (we read them ourselves); paranoid; the
 * versions host off; and every refused backend disabled.
 */
export function miseSettingsToml() {
    const list = (values) => `[${values.map((value) => JSON.stringify(value)).join(", ")}]`;
    return [
        "[settings]",
        "registry_floating = false",
        "all_compile = false",
        "not_found_auto_install = false",
        "idiomatic_version_file_enable_tools = []",
        "paranoid = true",
        "use_versions_host = false",
        "use_versions_host_track = false",
        `disable_backends = ${list(REFUSED_BACKENDS)}`,
        "",
        "[settings.aqua]",
        "baked_registry = true",
        "cosign = false",
        "slsa = false",
        "github_attestations = false",
        "",
    ].join("\n");
}
/**
 * Where a run's toolchain install keeps its generated `mise.toml` and the
 * `mise.lock` mise writes beside it: under the run user's mise data
 * directory, never the checkout, so mise never reads the repository's own
 * config. The setup run reads the lock back from here.
 */
export function toolchainLockDir(home) {
    return `${miseHome(home)}/yagni-toolchain`;
}
/** Where the template extracts a baked .NET SDK. */
export function dotnetBakedDir(version) {
    return `${QA_TOOLCHAIN.root}/dotnet/${version}`;
}
/**
 * The directory whose `bin/php` is a baked PHP line's CLI: the template links
 * Surý's `/usr/bin/php<line>` there, so the plan's PATH picks the line and
 * Composer (`#!/usr/bin/env php`) runs on it.
 */
export function phpBakedDir(line) {
    return `${QA_TOOLCHAIN.root}/php/${line}`;
}
/** Where the template installs the tools mise manages. */
function miseInstall(tool, version) {
    return `${MISE_BAKED_DIR}/installs/${tool}/${version}`;
}
/** The run user's own mise data directory: installs made during a run land here. */
function miseHome(home) {
    return `${home}/.local/share/mise`;
}
/** The run user's own uv Python directory: interpreters installed during a run land here. */
function uvPythonHome(home) {
    return `${home}/.local/share/uv/python`;
}
/**
 * The exact baked version a runtime selection names, or null when the
 * selection is a version the run installs. Only the baked lines are keys of
 * `QA_TOOLCHAIN`; every other value is an install.
 */
export function bakedRuntimeVersion(runtime, selection) {
    const baked = QA_TOOLCHAIN[runtime];
    return Object.hasOwn(baked, selection) ? baked[selection] : null;
}
/**
 * The precompiled Rubies a run may install: jdx/ruby's Linux x86_64 builds
 * (the source mise itself prefers), one per Ruby version, pinned to the
 * newest build revision's release tag and the SHA-256 GitHub records for its
 * tarball. The install checks the download against this digest before it
 * extracts anything, so a replaced release asset never runs.
 *
 * A Ruby version missing here is not installable yet (preflight says so).
 * To add the versions jdx/ruby has published since, list every release's
 * `ruby-<version>.x86_64_linux.tar.gz` asset with its `digest`
 * (`gh api --paginate repos/jdx/ruby/releases`), keep the highest `-<n>`
 * build revision per version, and add a row for each new version.
 */
export const QA_RUBY_BUILDS = {
    "3.2.1": { tag: "3.2.1-5", sha256: "e9724554c8a6d94cb25861825e0ef31c1c14d005f61ba6038801995df16168d9" },
    "3.2.2": { tag: "3.2.2-5", sha256: "9dedd33592234f856c0ec804e68590e6b4a98d10117e3ba5c85b93f17eb7dddf" },
    "3.2.3": { tag: "3.2.3-5", sha256: "90c7be8ca2fbacb8bac79a80c58665df5166676ccd919183c173f07471f84a5b" },
    "3.2.4": { tag: "3.2.4-5", sha256: "5499d5aaef52f7424558f5546cd67d960e9efecbbbcec2e2621083c80c389b19" },
    "3.2.5": { tag: "3.2.5-5", sha256: "75f92812db41b2181be2f62b3f342d954279329d115840c14c24d256689f58f0" },
    "3.2.6": { tag: "3.2.6-5", sha256: "a5948f39f879df72232a4845627d92b43bbe01c61a18a4318052b682fadedf50" },
    "3.2.7": { tag: "3.2.7-5", sha256: "933fe6d2efb348144d745a0ae0c1d4ebe5bd900cf55d7720c169884f5ea3e84f" },
    "3.2.8": { tag: "3.2.8-5", sha256: "c6e9281e3c1d98b6871e99da9c4ff0e9dec253db07ae5360561da6aaf958e581" },
    "3.2.9": { tag: "3.2.9-5", sha256: "af08f36d819fd4b1908304231713c106301da98f2bbedf3407340359c053d462" },
    "3.2.10": { tag: "3.2.10-5", sha256: "a47b390142514140d224ab4efed38bca98865335d8b02aa346a770fe3714a5c0" },
    "3.2.11": { tag: "3.2.11-6", sha256: "c4b28d885f18093bee686932a0e376fbc775ad42b394e4804b363ddf1d7d7c41" },
    "3.3.0": { tag: "3.3.0-5", sha256: "c756c1c73a00916453c3cac2f4a35fc2afaed78dca77002f7e4ef8cb0227dea5" },
    "3.3.1": { tag: "3.3.1-5", sha256: "a6b5337d7d577dc989f079c7e6b95e58bc878366a0d0c7652d2f75cee281df5d" },
    "3.3.2": { tag: "3.3.2-5", sha256: "a40f1a0cea4bbc0b8a1816bb4c19cc0a9733c573a2e82a7c545184888298ca22" },
    "3.3.3": { tag: "3.3.3-5", sha256: "6b9c247d16c4bb48dcbb7abaf49f5f94c461154fa4f210dab79d400c02a41c7a" },
    "3.3.4": { tag: "3.3.4-5", sha256: "5adbdc50ffd0e2907d42ffe4a1b511ab1232dee6891233026db52800f3f9d666" },
    "3.3.5": { tag: "3.3.5-5", sha256: "cd1bdef68baa8ebef5833360f3bc1161482e51379287e79deba2323266fe2a41" },
    "3.3.6": { tag: "3.3.6-5", sha256: "fec5a2ccae1926d51f7116ee6e1a30a4c5f28628013833eb6acdf8a0151d32cd" },
    "3.3.7": { tag: "3.3.7-5", sha256: "8868c61d1cde8e5d1b7115b6f812ac92abbd0043f5b76a0c9ff24d5431b778ae" },
    "3.3.8": { tag: "3.3.8-5", sha256: "3b1febbccf2293cb42bbb82538aa2f487c23771b3f4e30693a5dd93b818b715b" },
    "3.3.9": { tag: "3.3.9-5", sha256: "5dcdf65961e535296ce783c1d6aa0ac04df9023dd67fa3ed3356b05fed95fc3e" },
    "3.3.10": { tag: "3.3.10-5", sha256: "09d06c132a98a5c9ac462b2c5ebdf81e22205f26e62a4f591bffdfd1ece66100" },
    "3.3.11": { tag: "3.3.11-5", sha256: "7a0f8b0cf6bdaeb60e87d7aebe1fe00c530356651973d8b9d48f21f14d30086a" },
    "3.3.12": { tag: "3.3.12-1", sha256: "9f85eb7bce31098ba8d81081e047c795ab161a785b163b30eb1ce973b0e6c186" },
    "3.4.0": { tag: "3.4.0-5", sha256: "afff046b5448993062cd52ed215674c2b5f2966a94c2424584b39cb0ebc354cc" },
    "3.4.1": { tag: "3.4.1-5", sha256: "7e86c49cab7f17007b04b063927450afa9ede8722298baf81d1708f918730cf3" },
    "3.4.2": { tag: "3.4.2-5", sha256: "3dd386f9b8d41fb5f773525d9ed24cddc7db01e161622de5d83c6b657c8430af" },
    "3.4.3": { tag: "3.4.3-5", sha256: "e5b001aa889873ab71748b630f83cdc1757688bd72450af0118424b501ea4d3c" },
    "3.4.4": { tag: "3.4.4-5", sha256: "de455079a6acf966bd12c808b982e8cdfb059aaa1b9b9405d5326e968f3729da" },
    "3.4.5": { tag: "3.4.5-5", sha256: "77ebb56ee0136e90740f4a32a6084a6178aa8bf73ebabef5a6a46fa39e923670" },
    "3.4.6": { tag: "3.4.6-5", sha256: "24799653bf176271e3b8c2900ef45a47debbf66a68110e7138cdce07ac4d4d2a" },
    "3.4.7": { tag: "3.4.7-5", sha256: "c85445e2dabac8d5bc2b46fc330046df7f35c662f4711a45fe501435bc17cbd1" },
    "3.4.8": { tag: "3.4.8-5", sha256: "28a254a2295c64d46707600972a93c9be187673317b041adeeb571359400d07a" },
    "3.4.9": { tag: "3.4.9-6", sha256: "47ce5b193d7a835944f2fb30562a6494eaaed4490e7228a455a88bb0f4fa6ad0" },
    "3.4.10": { tag: "3.4.10-2", sha256: "81ce58b056ee2765e46785b4c6befb9852d9c3e50ff9385e7eb1cf3e0314d0bd" },
    "3.4.11": { tag: "3.4.11-1", sha256: "b25a69eb4004852bc57877047f23fc8b1300aab6d2245f37ea6563f19305070b" },
    "4.0.0": { tag: "4.0.0-5", sha256: "b5255223f039b1592803048ae630173dadd0c847a5314f4f775ff38578c29188" },
    "4.0.1": { tag: "4.0.1-5", sha256: "560e5385e980ae73dbcc0f280a9f1bdaf360bf8515539f76c39f522d63fc819d" },
    "4.0.2": { tag: "4.0.2-5", sha256: "54a5a1e9386f29dc88451a0eb5c37d6fa88f21d589e78eabea8c5cc350d284f1" },
    "4.0.3": { tag: "4.0.3-5", sha256: "aaf890d776c93c4601045f2a4bb484a6ab3eb0e656cd022b82b6e66c73a3cef2" },
    "4.0.4": { tag: "4.0.4-4", sha256: "c437d55e73f72b73cde1ff1ae1d2509e735d19b813e61f858334336b8b05399e" },
    "4.0.5": { tag: "4.0.5-3", sha256: "a39b9229d27868584fc44c803d68a2b5a9229c8ef75d826ff70d2fa2a9625592" },
    "4.0.6": { tag: "4.0.6-1", sha256: "aefa111873bac3b15c5c416bfaaca84e793d5312d4496d651ad79c44521b200a" },
    "4.0.7": { tag: "4.0.7-1", sha256: "32cd7e6ac93126e0f91dc40a0d2e6213f24a38be77937671e6cb82f82ea4ffe2" },
};
/**
 * The oldest version of each runtime a run can install: Node's oldest line
 * mise installs as a Linux build, python-build-standalone's oldest Python,
 * the oldest Ruby in `QA_RUBY_BUILDS`, the first Go shipped as a toolchain
 * module, and the first SDK of .NET 6, the oldest .NET line the run installs.
 * Preflight ends anything older with `qa_runtime_unsupported`.
 */
export const QA_RUNTIME_INSTALL_FLOOR = {
    node: "12.0.0",
    python: "3.8.0",
    ruby: "3.2.1",
    go: "1.21.0",
    dotnet: "6.0.100",
};
/** The runtimes a selection installs, in a fixed order; empty when everything is baked. */
export function runtimeInstalls(runtimes) {
    return RUNTIMES.flatMap((runtime) => {
        const version = runtimes[runtime];
        if (!version || bakedRuntimeVersion(runtime, version))
            return [];
        return [{ runtime, version, label: `Installing ${RUNTIME_NAMES[runtime]} ${version}` }];
    });
}
/**
 * The hosts an install downloads from, per runtime. Platform-owned: they are
 * added to a sandbox's egress only when its plan installs that runtime, and
 * never appear among a person's installation origins. A sandbox's egress is
 * fixed when it is created, so they stay reachable for the rest of that run,
 * like the registries: only the installers' own download switches are scoped
 * to the install step. Each is a public artifact host of the same kind as the
 * package registries a run already reaches (Wright already reaches github.com,
 * its repository host).
 *
 * - Node: mise fetches `index.json`, `SHASUMS256.txt` (and its signature) and
 *   the tarball, all from nodejs.org.
 * - Python: uv downloads python-build-standalone from Astral's mirror only;
 *   the install pins the mirror so uv never falls back to GitHub.
 * - Ruby: the pinned precompiled build from jdx/ruby's GitHub releases,
 *   checked against its pinned digest; github.com redirects each asset to
 *   release-assets.githubusercontent.com.
 * - Go: the baked Go downloads the newer toolchain as a module, from the Go
 *   module proxy (which serves from storage.googleapis.com), checked against
 *   the checksum database.
 * - .NET: the channel's release metadata and the SDK tarball it names, both
 *   from builds.dotnet.microsoft.com (no redirect), the tarball checked
 *   against the SHA-512 that metadata records.
 */
export const QA_RUNTIME_DOWNLOAD_HOSTS = {
    node: ["nodejs.org"],
    python: ["releases.astral.sh"],
    ruby: ["github.com", "release-assets.githubusercontent.com"],
    go: ["proxy.golang.org", "sum.golang.org", "storage.googleapis.com"],
    dotnet: [QA_DOTNET_DOWNLOAD_HOST],
    // Only the baked PHP lines run: preflight always chooses one of them.
    php: [],
};
/** The download hosts a selection's installs need, sorted; empty when nothing is installed. */
export function runtimeDownloadHosts(runtimes) {
    return [
        ...new Set(runtimeInstalls(runtimes).flatMap((install) => QA_RUNTIME_DOWNLOAD_HOSTS[install.runtime])),
    ].sort();
}
/** Astral's python-build-standalone mirror, pinned so uv never falls back to another host. */
const PYTHON_DOWNLOAD_MIRROR = "https://releases.astral.sh/github/python-build-standalone/releases/download";
/** jdx/ruby's precompiled Linux build of a pinned Ruby, from the release `QA_RUBY_BUILDS` names. */
function rubyDownloadUrl(version, tag) {
    return `https://github.com/jdx/ruby/releases/download/${tag}/ruby-${version}.x86_64_linux.tar.gz`;
}
/** Where an installed runtime lives under the run user's home. */
function installDir(install, home) {
    switch (install.runtime) {
        case "node":
        case "ruby":
            // mise links a partial version ("16") to the release it resolved.
            return `${miseHome(home)}/installs/${install.runtime}/${install.version}`;
        case "python": {
            // uv links the minor ("cpython-3.11-...") to the patch it installed.
            const [major, minor] = install.version.split(".");
            return `${uvPythonHome(home)}/cpython-${major}.${minor}-linux-x86_64-gnu`;
        }
        case "go":
            return `${home}/go/pkg/mod/golang.org/toolchain@v0.0.1-go${install.version}.linux-amd64`;
        case "dotnet":
            return `${home}/.local/share/dotnet/${install.version}`;
        case "php":
            // Never installed (see `runtimeInstallCommand`); named for completeness.
            return `${home}/.local/share/php/${install.version}`;
    }
}
/**
 * The script the .NET install runs with node to read a channel's release
 * metadata: the linux-x64 SDK tarball of the version asked for (a channel such
 * as `9.0` means that channel's newest SDK) and its SHA-512, printed as
 * `<url> <sha512>`. Only a tarball on the download host with a well-formed
 * digest is taken; anything else says no download was found. It holds no
 * single quote, so it travels inside one.
 */
const DOTNET_PICK_SCRIPT = [
    "const [file, asked] = process.argv.slice(1);",
    "let metadata;",
    'try { metadata = JSON.parse(require("fs").readFileSync(file, "utf8")); }',
    'catch { console.error("The .NET release metadata for " + asked + " could not be read."); process.exit(1); }',
    'const version = asked.split(".").length === 2 ? metadata["latest-sdk"] : asked;',
    "for (const release of metadata.releases || [])",
    "  for (const sdk of [release.sdk, ...(release.sdks || [])])",
    "    if (sdk && sdk.version === version)",
    "      for (const f of sdk.files || [])",
    String.raw `        if (f.rid === "linux-x64" && /\.tar\.gz$/.test(f.name) && /^https:\/\/builds\.dotnet\.microsoft\.com\//.test(f.url) && /^[a-f0-9]{128}$/.test(f.hash)) {`,
    '          console.log(f.url + " " + f.hash);',
    "          process.exit(0);",
    "        }",
    'console.error("No download found for .NET SDK " + asked);',
    "process.exit(1);",
].join("\n");
/**
 * The shell command that installs one runtime into the run user's home, run
 * unprivileged under the plan's toolchain environment. Only this command turns
 * the installers' downloads on: every later command keeps mise offline, uv's
 * downloads off and Go on its local toolchain. Every download is checked
 * before it runs: Node against SHASUMS256.txt (and its signature from 20),
 * Python against uv's pinned digests, Ruby against `QA_RUBY_BUILDS` and Go
 * against the checksum database. It runs from the home, so no version file in
 * a checkout can add tools to the install.
 */
export function runtimeInstallCommand(install, options) {
    const { home } = options;
    const dir = installDir(install, home);
    switch (install.runtime) {
        case "node":
            // Without the baked installs as shared directories: after an install
            // mise relinks every tool it can see, and the baked tree is read-only.
            return (`cd ${home} && env -u MISE_SHARED_INSTALL_DIRS MISE_DATA_DIR=${miseHome(home)} MISE_OFFLINE=0 MISE_YES=1 ` +
                "MISE_USE_VERSIONS_HOST=false MISE_USE_VERSIONS_HOST_TRACK=false MISE_NODE_COMPILE=false " +
                `mise install node@${install.version}`);
        case "python":
            return (`cd ${home} && UV_PYTHON_INSTALL_DIR=${uvPythonHome(home)} UV_PYTHON_DOWNLOADS=manual ` +
                `UV_PYTHON_INSTALL_MIRROR=${PYTHON_DOWNLOAD_MIRROR} uv python install --no-bin ${install.version}`);
        case "ruby": {
            const build = Object.hasOwn(QA_RUBY_BUILDS, install.version) ? QA_RUBY_BUILDS[install.version] : undefined;
            if (!build)
                return `echo 'Ruby ${install.version} has no precompiled build the run can install.' >&2; exit 1`;
            // Checked against the pinned digest before anything is extracted, and
            // extracted beside its final directory and moved into place, so an
            // interrupted download never leaves a directory that looks installed.
            const archive = `${dir}.partial.tar.gz`;
            return (`cd ${home} && if [ ! -x ${dir}/bin/ruby ]; then ` +
                `rm -rf ${dir}.partial && mkdir -p ${dir}.partial && ` +
                `curl -fsSL --retry 2 --connect-timeout 20 -o ${archive} ${rubyDownloadUrl(install.version, build.tag)} && ` +
                `printf '%s  %s\\n' ${build.sha256} ${archive} | sha256sum --check --quiet - && ` +
                `tar -xzf ${archive} -C ${dir}.partial --strip-components=1 && ` +
                `rm -f ${archive} && rm -rf ${dir} && mv ${dir}.partial ${dir}; fi && ${dir}/bin/ruby --version`);
        }
        case "go":
            return (`cd ${home} && GOTOOLCHAIN=go${install.version} ` +
                `${miseInstall("go", QA_TOOLCHAIN.go["1.23"])}/bin/go version`);
        case "dotnet": {
            // The channel's release metadata names the tarball and its SHA-512; the
            // tarball is checked against it before anything is extracted, then
            // extracted beside its final directory and moved into place.
            const channel = install.version.split(".").slice(0, 2).join(".");
            const metadata = `${dir}.partial.json`;
            const archive = `${dir}.partial.tar.gz`;
            return (`cd ${home} && if [ ! -x ${dir}/dotnet ]; then ` +
                `rm -rf ${dir}.partial && mkdir -p ${dir}.partial && ` +
                `status=$(curl -sSL --retry 2 --connect-timeout 20 -o ${metadata} -w '%{http_code}' ` +
                `https://${QA_DOTNET_DOWNLOAD_HOST}/dotnet/release-metadata/${channel}/releases.json) || ` +
                `{ echo 'The .NET ${channel} release metadata could not be downloaded from ${QA_DOTNET_DOWNLOAD_HOST}.' >&2; exit 1; }; ` +
                `if [ "$status" = 404 ]; then echo 'No download found for .NET SDK ${install.version}: Microsoft publishes no .NET ${channel}.' >&2; exit 1; fi; ` +
                `[ "$status" = 200 ] || { echo "The .NET release metadata answered HTTP $status." >&2; exit 1; }; ` +
                `pick=$(node -e '${DOTNET_PICK_SCRIPT}' ${metadata} ${install.version}) && ` +
                `curl -fsSL --retry 2 --connect-timeout 20 -o ${archive} "\${pick% *}" && ` +
                `printf '%s  %s\\n' "\${pick#* }" ${archive} | sha512sum --check --quiet - && ` +
                `tar -xzf ${archive} -C ${dir}.partial && ` +
                `rm -f ${archive} ${metadata} && rm -rf ${dir} && mv ${dir}.partial ${dir}; fi && ${dir}/dotnet --version`);
        }
        case "php":
            // PHP comes from the image's package archive at build time, so a run
            // has no way to add a line; preflight only ever chooses a baked one.
            return (`echo 'No download found for PHP ${install.version}: the sandbox has PHP ${Object.keys(QA_TOOLCHAIN.php).join(", ")} built in ` +
                "and installs no other PHP.' >&2; exit 1");
    }
}
/**
 * The install directory of each selected runtime: the baked one in the layout
 * the template writes, or, with `home`, the one a run's install writes under
 * the home. Node 24 is the image's own and has no directory here.
 */
export function toolchainRuntimeDirs(runtimes, options = {}) {
    const dirs = {};
    for (const runtime of RUNTIMES) {
        const selection = runtimes[runtime];
        if (!selection)
            continue;
        const baked = bakedRuntimeVersion(runtime, selection);
        if (baked === null) {
            if (options.home)
                dirs[runtime] = installDir({ runtime, version: selection }, options.home);
        }
        else if (runtime === "dotnet") {
            dirs.dotnet = dotnetBakedDir(baked);
        }
        else if (runtime === "php") {
            dirs.php = phpBakedDir(selection);
        }
        else if (runtime === "python") {
            dirs.python = `${UV_PYTHON_BAKED_DIR}/cpython-${baked}-linux-x86_64-gnu`;
        }
        else if (runtime !== "node" || selection !== "24") {
            dirs[runtime] = miseInstall(runtime, baked);
        }
    }
    return dirs;
}
/** A Ruby's own gem directory, read-only, searched after the writable home. */
function rubyGemDir(rubyDir, version) {
    const [major, minor] = version.split(".");
    return `${rubyDir}/lib/ruby/gems/${major}.${minor}.0`;
}
const LEGACY_RUNTIMES = RUNTIMES;
function isToolchainPlan(selection) {
    return "tools" in selection && Array.isArray(selection.tools);
}
/**
 * The runtime selection a plan's runtime tools make, keyed by line the way
 * `QaRuntimes` is: a baked exact version maps back to its line (`22.23.3` →
 * "22"), an install keeps its version. CLEANUP CHORE: goes with `QaRuntimes`.
 */
export function planRuntimes(plan) {
    const runtimes = {};
    for (const tool of plan.tools) {
        if (!LEGACY_RUNTIMES.includes(tool.name))
            continue;
        const runtime = tool.name;
        const lines = QA_TOOLCHAIN[runtime];
        const line = tool.origin === "baked" ? Object.keys(lines).find((key) => lines[key] === tool.version) : undefined;
        runtimes[runtime] = line ?? tool.version;
    }
    return runtimes;
}
/**
 * A runtime selection as a plan: each selected runtime, baked or installed
 * by the run's own installer, with that installer's download hosts. How every
 * stored revision without a plan, and every caller that still passes
 * `QaRuntimes`, reads as one.
 */
export function toolchainPlanFromRuntimes(runtimes, options = {}) {
    const tools = [];
    for (const runtime of RUNTIMES) {
        const selection = runtimes[runtime];
        if (!selection)
            continue;
        const baked = bakedRuntimeVersion(runtime, selection);
        tools.push(baked === null
            ? { name: runtime, version: selection, backend: "core", origin: "install", hosts: [...QA_RUNTIME_DOWNLOAD_HOSTS[runtime]] }
            : { name: runtime, version: baked, backend: "core", origin: "baked", hosts: [] });
    }
    const plan = { tools, unsatisfiable: [], notes: [], path: "", env: {} };
    return { ...plan, path: toolchainPath(plan, options), env: options.home ? toolchainEnv(plan, { home: options.home }) : {} };
}
/** Where `mise install <name>@<version>` puts a tool under the run user's home. */
export function miseToolInstallDir(home, name, version) {
    return `${miseHome(home)}/installs/${name.replace(/[:/]/g, "-")}/${version}`;
}
/**
 * The bin directories of a plan's installed tools beyond the runtimes (java,
 * kubectl, rust): a core tool keeps its commands in `bin`, an aqua package
 * either there or at its root (kubectl does).
 */
function planToolBins(plan, home) {
    return plan.tools
        .filter((tool) => tool.origin === "install" && !LEGACY_RUNTIMES.includes(tool.name))
        .flatMap((tool) => {
        const dir = miseToolInstallDir(home, tool.name, tool.version);
        return tool.backend === "core" || tool.backend.startsWith("core:") ? [`${dir}/bin`] : [`${dir}/bin`, dir];
    });
}
/**
 * One `PATH` for a run: the plan's installed tools, the toolchain's shared
 * bin (uv, mise, poetry), each selected runtime's bin, then the system
 * directories. With `home`, the writable bin directories package managers
 * install executables into come first among the runtime directories, so a
 * `gem install` or `go install` during setup is found by later commands, and
 * an installed runtime's bin stands where a baked one would. Takes a
 * `ToolchainPlan` or, for every caller that predates it, `QaRuntimes`.
 */
export function toolchainPath(selection, options = {}) {
    const runtimes = isToolchainPlan(selection) ? planRuntimes(selection) : selection;
    const dirs = toolchainRuntimeDirs(runtimes, options);
    const home = options.home;
    return [
        ...(home && isToolchainPlan(selection) ? planToolBins(selection, home) : []),
        `${QA_TOOLCHAIN.root}/bin`,
        ...(home && runtimes.ruby ? [`${home}/.gem/bin`] : []),
        ...(home && runtimes.go ? [`${home}/go/bin`] : []),
        // Where `dotnet tool install --global` puts its commands.
        ...(home && runtimes.dotnet ? [`${home}/.dotnet/tools`] : []),
        // Where `composer global require` puts its commands.
        ...(home && runtimes.php ? [`${home}/.config/composer/vendor/bin`] : []),
        ...RUNTIMES.flatMap((runtime) => {
            const dir = dirs[runtime];
            if (!dir)
                return [];
            // The SDK keeps `dotnet` at the root of its directory.
            return [runtime === "dotnet" ? dir : `${dir}/bin`];
        }),
        // Where a declared install line puts its command (`pip install --user`,
        // `cargo install`): on PATH whatever runtime the repository runs on, so
        // a bespoke CLI an environment declares is found by every later command,
        // but after the runtimes, so a `python` dropped there never shadows the
        // selected one.
        ...(home ? [`${home}/.local/bin`, `${home}/.cargo/bin`] : []),
        ...SYSTEM_PATH,
    ].join(":");
}
/** The newest of some dotted version lines: 8.10 is newer than 8.4. */
export function newestVersionLine(lines) {
    const parts = (line) => line.split(".").map(Number);
    return [...lines].sort((a, b) => {
        const [x, y] = [parts(a), parts(b)];
        for (let index = 0; index < Math.max(x.length, y.length); index += 1) {
            const diff = (x[index] ?? 0) - (y[index] ?? 0);
            if (diff !== 0)
                return diff;
        }
        return 0;
    }).at(-1);
}
/** The newest PHP line the image bakes. */
const NEWEST_BAKED_PHP = newestVersionLine(Object.keys(QA_TOOLCHAIN.php));
/** Whether a `PATH` already holds one of the baked PHP lines. */
function hasBakedPhp(dirs) {
    return dirs.some((dir) => dir.startsWith(`${QA_TOOLCHAIN.root}/php/`));
}
/**
 * `path` with the baked tools a plan did not select as a last resort, after
 * every other directory: a check that runs `php -l` on a plan with no PHP
 * finds the image's newest PHP instead of failing as not installed, while any
 * PHP the plan or the system provides still wins. `sandboxCommands` reads
 * this same `PATH`, so what a planner is told runs is what a build finds.
 */
export function withBakedFallbacks(path) {
    const dirs = path.split(":");
    if (!NEWEST_BAKED_PHP || hasBakedPhp(dirs))
        return path;
    return [...dirs, `${phpBakedDir(NEWEST_BAKED_PHP)}/bin`].join(":");
}
/**
 * `command` with the toolchain `PATH` exported inside it, the only way a
 * `PATH` reaches a sandbox command. E2B runs every command as
 * `/bin/bash -l -c <command>`, a login shell, and the node images'
 * `/etc/profile` resets `PATH` before the command starts, so a `PATH` passed
 * as a process variable is discarded. The other toolchain variables survive
 * that reset and still travel as process variables.
 */
export function withToolchainPath(command, path) {
    return `export PATH='${path.replaceAll("'", `'"'"'`)}'; ${command}`;
}
/**
 * The inverse of {@link withToolchainPath}'s prefix: the command a line was
 * built from, or null when the line carries no exported PATH. The one quoting
 * convention lives here, so a matcher that must recognise a command started
 * under an exported PATH (a running harness found after a deploy) never
 * re-encodes it.
 */
const EXPORTED_PATH_PREFIX = /^export PATH='(?:[^']|'"'"')*'; /;
export function stripExportedPathPrefix(line) {
    const match = EXPORTED_PATH_PREFIX.exec(line);
    return match ? line.slice(match[0].length) : null;
}
/**
 * The environment every setup command and service runs with, for the selected
 * runtimes. `home` is the run user's home: `/home/qaapp` for Fletcher and
 * `/home/user` for Wright. Nothing here points a writer into the toolchain,
 * and nothing here allows a download: only a plan's install step does, for
 * itself.
 */
export function toolchainEnv(selection, options) {
    const { home } = options;
    const runtimes = isToolchainPlan(selection) ? planRuntimes(selection) : selection;
    const dirs = toolchainRuntimeDirs(runtimes, { home });
    const env = {
        // Fail fast on an unapproved registry instead of spending npm's default
        // retry budget before the run can say which origin it needed.
        npm_config_fetch_retries: "1",
        npm_config_fetch_retry_mintimeout: "2000",
        npm_config_fetch_retry_maxtimeout: "5000",
        // The home's own installs first, then the baked ones, read-only.
        MISE_DATA_DIR: miseHome(home),
        MISE_SHARED_INSTALL_DIRS: `${MISE_BAKED_DIR}/installs`,
        MISE_YES: "0",
        MISE_OFFLINE: "1",
        // The baked policy: verifying backends only, no plugins, no auto-install.
        MISE_SYSTEM_CONFIG_FILE: QA_MISE_SETTINGS_PATH,
    };
    if (isToolchainPlan(selection)) {
        const java = selection.tools.find((tool) => tool.name === "java" && tool.origin === "install");
        if (java)
            env.JAVA_HOME = miseToolInstallDir(home, java.name, java.version);
    }
    if (runtimes.python) {
        const installed = bakedRuntimeVersion("python", runtimes.python) === null;
        Object.assign(env, {
            UV_PYTHON_DOWNLOADS: "never",
            UV_PYTHON_PREFERENCE: "only-managed",
            // An installed interpreter lives in the home; the baked ones never move.
            UV_PYTHON_INSTALL_DIR: installed ? uvPythonHome(home) : UV_PYTHON_BAKED_DIR,
            // The baked minor, so a patch pin in `.python-version` resolves to the
            // baked interpreter instead of attempting a download; or exactly what
            // the run installed.
            UV_PYTHON: runtimes.python,
            UV_CACHE_DIR: `${home}/.cache/uv`,
            UV_TOOL_DIR: `${home}/.local/share/uv/tools`,
            UV_PROJECT_ENVIRONMENT: ".venv",
            // A stale fork re-runs its install over the venv its snapshot kept, and
            // uv (0.8 on) refuses to replace an existing venv without --clear, so
            // `uv venv .venv && uv pip install ...` failed every stale Python fork
            // (the bench's python stack, 2026-10-23). Replace it, as uv used to; the
            // cache in the home keeps the reinstall quick.
            UV_VENV_CLEAR: "1",
            UV_HTTP_TIMEOUT: "30",
            POETRY_VIRTUALENVS_IN_PROJECT: "true",
            POETRY_CACHE_DIR: `${home}/.cache/pypoetry`,
            PIP_DEFAULT_TIMEOUT: "15",
            PIP_RETRIES: "1",
        });
    }
    if (runtimes.ruby && dirs.ruby)
        Object.assign(env, {
            // Bundler's version switch and any `gem install` land in the home.
            GEM_HOME: `${home}/.gem`,
            GEM_PATH: `${home}/.gem:${rubyGemDir(dirs.ruby, bakedRuntimeVersion("ruby", runtimes.ruby) ?? runtimes.ruby)}`,
            BUNDLE_PATH: "vendor/bundle",
            BUNDLE_USER_HOME: `${home}/.bundle`,
        });
    if (runtimes.go)
        Object.assign(env, {
            // The selected toolchain wins; a newer `toolchain` line never downloads.
            GOTOOLCHAIN: "local",
            GOPATH: `${home}/go`,
            GOMODCACHE: `${home}/go/pkg/mod`,
            GOCACHE: `${home}/.cache/go-build`,
        });
    if (runtimes.dotnet && dirs.dotnet)
        Object.assign(env, {
            DOTNET_ROOT: dirs.dotnet,
            DOTNET_CLI_TELEMETRY_OPTOUT: "1",
            DOTNET_NOLOGO: "1",
            DOTNET_SKIP_FIRST_TIME_EXPERIENCE: "1",
            DOTNET_GENERATE_ASPNET_CERTIFICATE: "false",
            // The baked SDK is read-only: nothing checks it for workload updates.
            DOTNET_CLI_WORKLOAD_UPDATE_NOTIFY_DISABLE: "1",
            NUGET_PACKAGES: `${home}/.nuget/packages`,
            // Signed-package revocation checks reach certificate hosts no sandbox
            // opens; offline, NuGet checks what the package itself carries.
            NUGET_CERT_REVOCATION_MODE: "offline",
            // A test project on an older .NET than the SDK carries runs on the
            // SDK's own runtime instead of failing for a runtime it lacks.
            DOTNET_ROLL_FORWARD: "Major",
            // No MSBuild node or compiler server outlives the command that started it.
            MSBUILDDISABLENODEREUSE: "1",
            DOTNET_CLI_USE_MSBUILD_SERVER: "0",
            UseSharedCompilation: "false",
        });
    if (runtimes.php)
        Object.assign(env, {
            // Composer's home and cache live in the run user's home, never the toolchain.
            COMPOSER_HOME: `${home}/.config/composer`,
            COMPOSER_CACHE_DIR: `${home}/.cache/composer`,
            // A run has nobody to answer a prompt.
            COMPOSER_NO_INTERACTION: "1",
            // The advisory check after an install calls packagist.org's API and
            // never changes what was installed.
            COMPOSER_NO_AUDIT: "1",
        });
    return env;
}
/**
 * What every build sandbox has on `PATH`, by command name: the image's own
 * Node, the package managers and installers this toolchain bakes, OpenTofu
 * and TFLint from the toolchain's bin (on every build's `PATH`), the
 * distribution's Python, the build essentials, Docker, `psql` at the managed
 * Postgres's major (`postgresClientBakeScript` in infra/e2b/toolchain.ts) and
 * the pi image's `gh` (`PI_GH_PATH` in infra/e2b/pi/runtime.ts,
 * /usr/local/bin). Kept beside `QA_TOOLCHAIN` so a tool the template adds is
 * named here in the same change; the pi image smoke (`assertSandboxCommands`)
 * fails when this list and the image disagree.
 */
const SANDBOX_BASE_COMMANDS = [
    "node", "npm", "npx", "pnpm", "yarn", "yarn1", "python3", "uv", "uvx", "poetry",
    "tofu", "tflint", "git", "make", "gcc", "bash", "curl", "jq", "docker", "gh", "psql",
];
/** A runtime's own commands, on `PATH` when its directory is. */
const RUNTIME_COMMANDS = {
    node: [],
    python: ["python", "pip"],
    ruby: ["ruby", "gem", "bundle", "rake"],
    go: ["go", "gofmt"],
    dotnet: ["dotnet"],
    // Composer runs on whichever PHP is on PATH, so it is there exactly when PHP is.
    php: ["php", "composer"],
};
/**
 * Commands a plan's checks reach for that no build sandbox has. Not a full
 * list (nothing not in `available` is there); these are the ones worth
 * naming to a planner, since a plan would otherwise assume them. A runtime's
 * commands are named through the runtime when its directory is not on PATH.
 */
const COMMONLY_ASSUMED_COMMANDS = [
    "terraform", "java", "mvn", "gradle", "cargo", "rustc",
    "kubectl", "helm", "aws", "gcloud", "swift", "elixir", "mix",
];
/** The commands a plan tool is run as, when they differ from its name. */
const PLAN_TOOL_COMMANDS = {
    opentofu: ["tofu"],
    java: ["java", "javac"],
    rust: ["cargo", "rustc"],
    elixir: ["elixir", "mix"],
};
/**
 * The runtimes a build's checks find on `PATH`: the selected ones, and each
 * baked fallback `withBakedFallbacks` adds. Read from that `PATH` itself, so a
 * fallback added or dropped there changes what the planner is told with it.
 */
function runtimesOnPath(runtimes) {
    const onPath = new Set(RUNTIMES.filter((runtime) => runtimes[runtime]));
    if (hasBakedPhp(withBakedFallbacks(toolchainPath(runtimes)).split(":")))
        onPath.add("php");
    return onPath;
}
/**
 * What a build's checks can run, by command name, for the selected runtimes
 * or a plan (whose resolved tools are on PATH too):
 * `available` is on `PATH`, `absent` is what plans commonly assume that is
 * not (a runtime whose directory is not on the build's `PATH` is absent too).
 * Derived from the `PATH` the build's checks run on (`toolchainPath` plus
 * `withBakedFallbacks`), never from a booted sandbox; the pi image smoke
 * checks both lists against the image, and the build's own probe of each
 * check (`probeChecks` in the backend) is what decides. With the repository's
 * "what runs here" record, what its sandbox answered on PATH is added to
 * `available` and dropped from `absent`. The record keeps only what it found,
 * so a command it never swept is never read as missing.
 */
export function sandboxCommands(selection, options = {}) {
    const runtimes = isToolchainPlan(selection) ? planRuntimes(selection) : selection;
    // A plan's resolved tools beyond the runtimes, by the command each is run as.
    const planned = isToolchainPlan(selection)
        ? selection.tools
            .filter((tool) => !LEGACY_RUNTIMES.includes(tool.name))
            .flatMap((tool) => PLAN_TOOL_COMMANDS[tool.name] ?? [tool.name.replace(/^.*[:/]/, "")])
        : [];
    const onPath = (options.record?.toolsOnPath ?? []).map((tool) => tool.name.trim()).filter(Boolean);
    const found = runtimesOnPath(runtimes);
    const available = [
        ...onPath,
        ...SANDBOX_BASE_COMMANDS,
        ...RUNTIMES.flatMap((runtime) => (found.has(runtime) ? RUNTIME_COMMANDS[runtime] : [])),
        ...planned,
    ];
    const absent = [
        ...COMMONLY_ASSUMED_COMMANDS,
        ...RUNTIMES.flatMap((runtime) => (found.has(runtime) ? [] : RUNTIME_COMMANDS[runtime].filter((name) => name !== "pip"))),
    ].filter((name) => !available.includes(name));
    return { available: [...new Set(available)], absent: [...new Set(absent)] };
}
/**
 * What a run's commands find on `PATH`, one sentence each, generated from
 * `QA_TOOLCHAIN` and the run's runtimes so it cannot drift from what the
 * template bakes and the plan installs: the runtime versions (built in or
 * installed by this run), what each package-manager binary is, and that
 * runtimes change only in the plan's install step. The repair model reads
 * these instead of guessing at the sandbox.
 */
export function toolchainFacts(runtimes) {
    const facts = [];
    for (const runtime of RUNTIMES) {
        const selection = runtimes[runtime];
        const name = RUNTIME_NAMES[runtime];
        if (!selection) {
            if (runtime === "node")
                facts.push(`node: Node ${QA_TOOLCHAIN.node["24"]}, the sandbox's own; the repository pins no Node.`);
            if (runtime === "php" && NEWEST_BAKED_PHP)
                facts.push(`php: PHP ${NEWEST_BAKED_PHP}, the newest built in, last on PATH since the repository selects no PHP; composer runs on it.`);
            continue;
        }
        const baked = bakedRuntimeVersion(runtime, selection);
        facts.push(baked
            ? `${runtime}: ${name} ${baked}, built in.`
            : `${runtime}: ${name} ${selection}, installed by this run before the setup commands.`);
    }
    const node = runtimes.node ?? "24";
    const nodeMajor = Number((bakedRuntimeVersion("node", node) ?? node).split(".")[0]);
    facts.push(`yarn: Yarn ${QA_TOOLCHAIN.yarn1} in a checkout whose package.json pins yarn@1, or whose yarn.lock is a classic "# yarn lockfile v1" with no packageManager pin; ` +
        `Yarn ${QA_TOOLCHAIN.yarnBerry} for yarn@2+ pins, Yarn 2+ lockfiles and .yarnrc.yml (it honours a committed yarnPath). ` +
        (nodeMajor < 18
            ? `Yarn ${QA_TOOLCHAIN.yarnBerry} needs Node 18 or newer, so on this run's Node only Yarn 1 or a committed yarnPath release runs.`
            : "The checkout the command runs in decides, so `yarn install` is right for either kind."), `yarn1: always Yarn ${QA_TOOLCHAIN.yarn1}.`, `pnpm: pnpm ${QA_TOOLCHAIN.pnpm}. npm: the npm that ships with the run's Node.`, `poetry ${QA_TOOLCHAIN.poetry} and uv ${QA_TOOLCHAIN.uv} are on PATH; Python commands use the checkout's .venv.`, ...(runtimes.php
        ? [
            `composer: Composer ${QA_TOOLCHAIN.composer}, on the run's PHP; \`composer install\` installs from packagist.org into the checkout's vendor/, and a PHPUnit suite runs as vendor/bin/phpunit. ` +
                `The PHP lines built in are ${Object.keys(QA_TOOLCHAIN.php).join(", ")}, each with ${QA_PHP_MODULES.join(", ")}; ` +
                "a setup command cannot add a PHP extension.",
        ]
        : []), `tofu: OpenTofu ${QA_TOOLCHAIN.opentofu} and tflint: TFLint ${QA_TOOLCHAIN.tflint} are on PATH. ` +
        "HashiCorp's terraform is not, so a Terraform configuration runs with tofu (`tofu init`, `tofu validate`, `tofu plan`), and providers install from registry.opentofu.org.", ...(runtimes.dotnet
        ? [
            "dotnet runs with DOTNET_ROLL_FORWARD=Major, so a test project that targets an older .NET runs on the SDK's own runtime. " +
                "NuGet restores into ~/.nuget/packages from api.nuget.org and the installation origins the environment approves.",
        ]
        : []), `NuGet, npm and RubyGems sources on GitHub Packages sign in with the workspace variable ${GITHUB_PACKAGES_TOKEN_VARIABLE}, through the run user's own ~/.nuget/NuGet/NuGet.Config, ~/.npmrc and ~/.yarnrc.yml and bundler's BUNDLE_RUBYGEMS__PKG__GITHUB__COM, and any variable the repository's own config names for those sources is filled from it. ` +
        "Never write a credential into a repository file; a missing token is a person's to set, not a command to add.", "Runtimes are installed only by the run's own install step, before the setup commands, from the versions the repository pins. " +
        "Setup commands cannot download, install or switch a runtime (mise and uv are offline for them), " +
        "so never add such a command: a wrong runtime is fixed in the repository's version files, not by a setup command.");
    return facts;
}
/** The registries each ecosystem installs from by default. */
export const QA_ECOSYSTEM_ORIGINS = {
    npm: ["https://registry.npmjs.org"],
    yarn: ["https://registry.yarnpkg.com"],
    pypi: ["https://pypi.org", "https://files.pythonhosted.org"],
    rubygems: ["https://rubygems.org", "https://index.rubygems.org"],
    go: ["https://proxy.golang.org", "https://sum.golang.org", "https://storage.googleapis.com"],
    // The v3 service index and every resource a restore reads (package
    // downloads, registrations) are served from this one host, without redirects.
    nuget: ["https://api.nuget.org"],
    // Composer reads package metadata from Packagist and downloads each
    // GitHub-hosted package's archive through GitHub's API, which redirects to
    // codeload.github.com.
    composer: ["https://repo.packagist.org", "https://packagist.org", "https://api.github.com", "https://codeload.github.com"],
    // `tofu init` reads providers and modules from the OpenTofu registry, which
    // points each provider download at its GitHub release (github.com, which
    // redirects to release-assets.githubusercontent.com or
    // objects.githubusercontent.com); `tflint --init` reads plugin releases
    // through api.github.com.
    opentofu: [
        "https://registry.opentofu.org",
        "https://github.com",
        "https://api.github.com",
        "https://release-assets.githubusercontent.com",
        "https://objects.githubusercontent.com",
    ],
};
/** The ecosystem each runtime installs from. */
export function runtimeInstallationOrigins(runtimes) {
    return [
        ...(runtimes.python ? QA_ECOSYSTEM_ORIGINS.pypi : []),
        ...(runtimes.ruby ? QA_ECOSYSTEM_ORIGINS.rubygems : []),
        ...(runtimes.go ? QA_ECOSYSTEM_ORIGINS.go : []),
        ...(runtimes.dotnet ? QA_ECOSYSTEM_ORIGINS.nuget : []),
        ...(runtimes.php ? QA_ECOSYSTEM_ORIGINS.composer : []),
    ];
}
/** A file OpenTofu or Terraform reads: a configuration, its variables, or the dependency lock. */
export function isTerraformFile(name) {
    return /\.(?:tf|tofu|tfvars)(?:\.json)?$/i.test(name) || name === ".terraform.lock.hcl";
}
/** A file a .NET build reads: a project, a solution, or the SDK's and NuGet's own files. */
export function isDotnetFile(name) {
    return (/\.(?:csproj|fsproj|vbproj|sln|slnx)$/i.test(name) ||
        ["global.json", "packages.lock.json", "directory.packages.props", "nuget.config"].includes(name.toLowerCase()));
}
/**
 * The installation origins a repository's own files imply, derived from the
 * platform, never from anything the repository declares. `files` are paths in
 * the checkout; only their names are read. npm's registry is always included:
 * every sandbox can reach it and `npx` installs from it.
 */
export function lockfileInstallationOrigins(files) {
    const names = new Set();
    for (const file of files)
        names.add(file.slice(file.lastIndexOf("/") + 1));
    const has = (...candidates) => candidates.some((name) => names.has(name));
    const python = has("uv.lock", "poetry.lock", "pyproject.toml", "Pipfile", "Pipfile.lock") ||
        [...names].some((name) => /^requirements.*\.txt$/.test(name));
    return [
        ...new Set([
            ...QA_ECOSYSTEM_ORIGINS.npm,
            ...(has("yarn.lock") ? QA_ECOSYSTEM_ORIGINS.yarn : []),
            ...(python ? QA_ECOSYSTEM_ORIGINS.pypi : []),
            ...(has("Gemfile", "Gemfile.lock") ? QA_ECOSYSTEM_ORIGINS.rubygems : []),
            ...(has("go.mod", "go.sum") ? QA_ECOSYSTEM_ORIGINS.go : []),
            ...([...names].some(isDotnetFile) ? QA_ECOSYSTEM_ORIGINS.nuget : []),
            ...(has("composer.json", "composer.lock") ? QA_ECOSYSTEM_ORIGINS.composer : []),
            ...([...names].some(isTerraformFile) ? QA_ECOSYSTEM_ORIGINS.opentofu : []),
        ]),
    ];
}
//# sourceMappingURL=qaToolchain.js.map